// 통합통관 청구서 고객별 나누기(2026-10-07 YZY) — 순수 함수 모듈(브라우저·node 공용). 화면은 LclBillSplit.vue.
//
// 올리는 파일 = 청구서 서식 시트(【열이름】 자리표시자) + LCL 시트(1행 적용환율, 「No.」 헤더 행, 그 아래 고객별 1행).
// 고객 행마다 서식 시트의 자리표시자를 그 행 값으로 채워 고객 1명 = 파일 1개를 만든다.
//
// ⚠ 시스템 저장 안 함 — 나중에 출고 청구 자동생성으로 대체할 임시 도구다.
//   견적서 4종(docs/coupass-logistics-chain.md §2·§4)은 발주(Reqpurchase) 단위 그릇인데 이 표는 고객×날짜 합계라
//   그대로 담을 그릇이 없다(발주로 나누는 배분 경로를 새로 만들어야 한다).
//
// 서식(병합·테두리·글꼴·열너비)·수식을 그대로 두려고 SheetJS 로 다시 쓰지 않고 원본 zip 의 시트 XML 만 고친다.
// (SheetJS CE 는 서식을 못 쓴다 — invoiceXlsx.js 와 같은 이유. 새 라이브러리 없이 내장 CFB 로 읽고 쓴다)
import * as XLSX from 'xlsx'

const PH_RE = /【([^【】]+)】/g
const enc = new TextEncoder()
const dec = new TextDecoder('utf-8')

function text(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\r/g, '').trim()
}
// 자리표시자·헤더 비교 키 — 공백·줄바꿈 차이는 같은 이름으로 본다
export function phKey(s) {
  return text(s).replace(/\s+/g, '').toLowerCase()
}
function xmlEsc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
function xmlUnesc(s) {
  return String(s).replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (m, n) => String.fromCharCode(+n)).replace(/&#x([0-9a-f]+);/gi, (m, n) => String.fromCharCode(parseInt(n, 16)))
    .replace(/&amp;/g, '&')
}
function isEmpty(v) {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
}

// ───────────────────────── 원본 zip 읽기 ─────────────────────────

function readZip(u8) {
  const cfb = XLSX.CFB.read(u8, { type: 'array' })
  const get = p => {
    const e = XLSX.CFB.find(cfb, '/' + p)
    return e && e.content ? dec.decode(e.content instanceof Uint8Array ? e.content : new Uint8Array(e.content)) : ''
  }
  return { cfb, get }
}

// 시트 이름 → 시트 XML 경로(xl/worksheets/sheetN.xml)
function sheetPaths(get) {
  const wbXml = get('xl/workbook.xml')
  const relXml = get('xl/_rels/workbook.xml.rels')
  const rel = {}
  relXml.replace(/<Relationship\b[^>]*>/g, tag => {
    const id = (tag.match(/\bId="([^"]+)"/) || [])[1]
    const target = (tag.match(/\bTarget="([^"]+)"/) || [])[1]
    if (id && target) rel[id] = target.replace(/^\/?xl\//, '').replace(/^\//, '')
    return tag
  })
  const out = {}
  wbXml.replace(/<sheet\b[^>]*>/g, tag => {
    const name = xmlUnesc((tag.match(/\bname="([^"]*)"/) || [])[1] || '')
    const rid = (tag.match(/\br:id="([^"]+)"/) || [])[1]
    if (name && rid && rel[rid]) out[name] = 'xl/' + rel[rid]
    return tag
  })
  return out
}

function sharedStrings(get) {
  const xml = get('xl/sharedStrings.xml')
  const list = []
  xml.replace(/<si>([\s\S]*?)<\/si>/g, (m, inner) => {
    let s = ''
    inner.replace(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g, (mm, t) => { s += xmlUnesc(t); return mm })
    // 윗주(rPh) 안의 글자는 빼야 한다 — rPh 블록을 먼저 지운 뒤 다시 모은다
    if (/<rPh\b/.test(inner)) {
      s = ''
      inner.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '').replace(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g, (mm, t) => { s += xmlUnesc(t); return mm })
    }
    list.push(s)
    return m
  })
  return list
}

// 셀 XML 의 글자(공유문자열·인라인) — 자리표시자 찾기용
function cellText(cellXml, sst) {
  const t = (cellXml.match(/^<c\b[^>]*\bt="([^"]+)"/) || [])[1]
  if (t === 's') {
    const v = (cellXml.match(/<v>([^<]*)<\/v>/) || [])[1]
    return v !== undefined ? (sst[+v] || '') : ''
  }
  if (t === 'inlineStr' || t === 'str') {
    let s = ''
    cellXml.replace(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g, (m, x) => { s += xmlUnesc(x); return m })
    if (!s && t === 'str') s = xmlUnesc((cellXml.match(/<v>([^<]*)<\/v>/) || [])[1] || '')
    return s
  }
  return ''
}

// ───────────────────────── 파싱 ─────────────────────────

// 올린 파일 → { template, data, rate, header[], rows[], placeholders[], mapping }
// template = 【】 자리표시자가 있는 첫 시트, data = 이름이 LCL 인 시트(없으면 「No.」 헤더가 있는 다른 시트)
export function parseBillWorkbook(u8) {
  const { get } = readZip(u8)
  const paths = sheetPaths(get)
  const sst = sharedStrings(get)
  const wb = XLSX.read(u8, { type: 'array', cellNF: true })
  const names = wb.SheetNames

  const phOf = name => {
    const xml = get(paths[name] || '')
    const found = []
    ;(xml.match(/<c\b[^>]*\/>|<c\b[^>]*>[\s\S]*?<\/c>/g) || []).forEach(c => {
      const s = cellText(c, sst)
      let m
      PH_RE.lastIndex = 0
      while ((m = PH_RE.exec(s))) found.push(m[1])
    })
    return found
  }
  let template = null
  let placeholders = []
  for (const n of names) {
    const ph = phOf(n)
    if (ph.length) { template = n; placeholders = ph; break }
  }
  if (!template) throw new Error('没有找到【】占位符的模板表(【열이름】 자리표시자가 있는 청구서 시트를 못 찾았습니다)')

  const headerRowOf = ws => {
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' })
    for (let r = 0; r < Math.min(aoa.length, 10); r++) {
      if (phKey(aoa[r] && aoa[r][0]) === 'no.' || phKey(aoa[r] && aoa[r][0]) === 'no') return r
    }
    return -1
  }
  let data = names.find(n => n !== template && phKey(n) === 'lcl')
  if (!data) data = names.find(n => n !== template && headerRowOf(wb.Sheets[n]) >= 0)
  if (!data) throw new Error('没有找到LCL表(LCL 시트를 못 찾았습니다)')
  const ws = wb.Sheets[data]
  const hr = headerRowOf(ws)
  if (hr < 0) throw new Error('LCL表没有「No.」表头(LCL 시트에 「No.」 헤더 행이 없습니다)')

  const range = XLSX.utils.decode_range(ws['!ref'])
  const header = []
  for (let c = 0; c <= range.e.c; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: hr, c })]
    header.push(cell ? text(cell.v) : '')
  }
  // 적용환율 = 헤더 위 행에서 「환율」 옆 숫자
  let rate = null
  for (let r = 0; r < hr && rate === null; r++) {
    for (let c = 0; c < range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })]
      if (cell && /환율|汇率/.test(text(cell.v))) {
        const nx = ws[XLSX.utils.encode_cell({ r, c: c + 1 })]
        if (nx && typeof nx.v === 'number') { rate = nx.v; break }
      }
    }
  }

  // 고객 행 — No.(고객명) 칸이 빈 행은 건너뛴다. 값은 원본 그대로(숫자는 숫자, 서식 글자는 w)
  const rows = []
  for (let r = hr + 1; r <= range.e.r; r++) {
    const a = ws[XLSX.utils.encode_cell({ r, c: 0 })]
    const name = a ? text(a.w !== undefined ? a.w : a.v) : ''
    if (!name) continue
    const cells = []
    for (let c = 0; c < header.length; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })]
      cells.push(cell ? { v: cell.v, t: cell.t, w: cell.w } : null)
    }
    rows.push({ sheetRow: r + 1, name, cells })
  }

  const mapping = buildMapping(placeholders, header)
  return { template, data, headerRow: hr + 1, rate, header, rows, placeholders, mapping, paths }
}

// 자리표시자 → 헤더 열(이름 일치). 같은 이름 열이 둘 이상이면 전부 기억해 두고 값이 있는 첫 열을 쓴다
export function buildMapping(placeholders, header) {
  const cols = {}
  header.forEach((h, c) => {
    const k = phKey(h)
    if (!k) return
    ;(cols[k] = cols[k] || []).push(c)
  })
  const map = {}
  const uniq = []
  placeholders.forEach(p => { if (!uniq.includes(p)) uniq.push(p) })
  uniq.forEach(p => { map[p] = cols[phKey(p)] || null })
  return {
    byName: map,
    matched: uniq.filter(p => map[p]),
    unmatched: uniq.filter(p => !map[p])
  }
}

// 그 행에서 자리표시자 값 — 매핑 못 함·빈 값은 null(빈칸), 0 은 0
export function valueFor(row, mapping, ph) {
  const cs = mapping.byName[ph]
  if (!cs) return null
  for (const c of cs) {
    const cell = row.cells[c]
    if (cell && !isEmpty(cell.v)) return cell
  }
  return null
}

// ───────────────────────── 날짜·파일명 ─────────────────────────

export function rowDate(row, header) {
  const c = header.findIndex(h => phKey(h) === '날짜' || phKey(h) === '日期')
  const cell = c >= 0 ? row.cells[c] : null
  if (!cell || isEmpty(cell.v)) return ''
  if (typeof cell.v === 'number') return XLSX.SSF.format('yyyy-mm-dd', cell.v)
  return text(cell.w || cell.v)
}

// 파일명에 못 쓰는 문자는 _ 로(2026-10-07 YZY)
export function safeFileName(s) {
  return String(s == null ? '' : s).replace(/[\\/:*?"<>|\r\n]/g, '_').trim()
}

// 고객별 파일명 = 고객명_업로드파일명 (예: objective_(화주건)청구서_20250711.xlsx). 확장자는 업로드 파일 그대로.
export function billFileName(row, sourceName) {
  const src = safeFileName(sourceName) || '통합통관청구서.xlsx'
  return safeFileName(row.name) + '_' + (/\.[^.]+$/.test(src) ? src : src + '.xlsx')
}

// 여러 개 묶음 zip 파일명 = 업로드 파일명에서 확장자만 .zip (예: (화주건)청구서_20250711.zip)
export function zipFileName(sourceName) {
  return (safeFileName(sourceName).replace(/\.[^.]+$/, '') || '통합통관청구서') + '.zip'
}

// ───────────────────────── 고객 1명 파일 만들기 ─────────────────────────

// 서식 시트: 자리표시자 셀만 값으로 바꾼다. 셀 스타일(s)은 그대로 둔다 → 서식·테두리·날짜 형식 유지.
//  - 셀 전체가 자리표시자 하나 → 원본 값 형태(숫자는 숫자, 글자는 글자). 빈 값·매핑 실패 = 빈칸
//  - 글자 사이에 섞여 있으면 → 글자 치환(값은 화면에 보이던 글자 w)
//  - 수식 셀은 값 캐시를 지워 열 때 다시 계산하게 한다(workbook calcPr fullCalcOnLoad)
function fillTemplateXml(xml, row, mapping, sst) {
  return xml.replace(/<c\b[^>]*\/>|<c\b[^>]*>[\s\S]*?<\/c>/g, c => {
    if (/<f[\s>]/.test(c)) return c.replace(/<v>[^<]*<\/v>/, '')
    const s = cellText(c, sst)
    if (!s || s.indexOf('【') < 0) return c
    const open = c.match(/^<c\b[^>]*?(\/?)>/)[0]
    const ref = (open.match(/\br="([^"]+)"/) || [])[1]
    const st = (open.match(/\bs="([^"]+)"/) || [])[1]
    const attrs = 'r="' + ref + '"' + (st !== undefined ? ' s="' + st + '"' : '')
    const whole = s.trim().match(/^【([^【】]+)】$/)
    if (whole) {
      const cell = valueFor(row, mapping, whole[1])
      if (!cell) return '<c ' + attrs + '/>'
      if (typeof cell.v === 'number') return '<c ' + attrs + '><v>' + cell.v + '</v></c>'
      if (typeof cell.v === 'boolean') return '<c ' + attrs + ' t="b"><v>' + (cell.v ? 1 : 0) + '</v></c>'
      return '<c ' + attrs + ' t="inlineStr"><is><t xml:space="preserve">' + xmlEsc(text(cell.w !== undefined ? cell.w : cell.v)) + '</t></is></c>'
    }
    const out = s.replace(PH_RE, (m, p) => {
      const cell = valueFor(row, mapping, p)
      return cell ? text(cell.w !== undefined ? cell.w : cell.v) : ''
    })
    return '<c ' + attrs + ' t="inlineStr"><is><t xml:space="preserve">' + xmlEsc(out) + '</t></is></c>'
  })
}

// LCL 시트: 헤더 위·헤더 행 + 이 고객 행만 남긴다(다른 고객 금액이 새지 않게).
// 고객 행은 헤더 바로 아래로 옮긴다 — 서식 시트 수식이 LCL!B1(환율)처럼 헤더 위 칸을 보므로 그 칸은 그대로다
function keepOneRowXml(xml, headerRow, sheetRow) {
  const target = headerRow + 1
  const sd = xml.match(/<sheetData>([\s\S]*?)<\/sheetData>|<sheetData\/>/)
  if (!sd) return xml
  const rows = sd[1] ? (sd[1].match(/<row\b[^>]*\/>|<row\b[^>]*>[\s\S]*?<\/row>/g) || []) : []
  const kept = []
  rows.forEach(rx => {
    const r = +((rx.match(/^<row\b[^>]*\br="(\d+)"/) || [])[1] || 0)
    if (r && r <= headerRow) kept.push(rx)
    else if (r === sheetRow) {
      kept.push(rx.replace(/^(<row\b[^>]*\br=")\d+"/, '$1' + target + '"')
        .replace(/(<c\b[^>]*\br="[A-Z]+)\d+"/g, '$1' + target + '"')
        // 같은 행을 가리키는 수식(예: =D5*200)이 있으면 행 번호를 옮긴 행으로 바꾼다
        .replace(/<f>([^<]*)<\/f>/g, (m, f) => '<f>' + f.replace(new RegExp('(\\$?[A-Z]{1,3}\\$?)' + sheetRow + '(?![0-9])', 'g'), '$1' + target) + '</f>'))
    }
  })
  let out = xml.replace(sd[0], '<sheetData>' + kept.join('') + '</sheetData>')
  out = out.replace(/<dimension ref="([A-Z]+\d+):([A-Z]+)\d+"\/>/, (m, a, b) => '<dimension ref="' + a + ':' + b + target + '"/>')
  out = out.replace(/(<pane\b[^>]*\btopLeftCell=")([A-Z]+)\d+"/, '$1$2' + target + '"')
  // 남은 행 밖을 가리키는 병합·자동필터·조건부서식은 지운다(엑셀이 「복구」 창을 띄우지 않게)
  out = out.replace(/<autoFilter\b[^>]*\/>|<autoFilter\b[\s\S]*?<\/autoFilter>/g, '')
  out = out.replace(/<conditionalFormatting\b[\s\S]*?<\/conditionalFormatting>/g, '')
  out = out.replace(/<dataValidations\b[\s\S]*?<\/dataValidations>/g, '')
  out = out.replace(/<mergeCell ref="[A-Z]+(\d+):[A-Z]+(\d+)"\/>/g, (m, a, b) => (+a > headerRow || +b > headerRow) ? '' : m)
  out = out.replace(/<mergeCells count="\d+">([\s\S]*?)<\/mergeCells>/, (m, inner) => {
    const n = (inner.match(/<mergeCell\b/g) || []).length
    return n ? '<mergeCells count="' + n + '">' + inner + '</mergeCells>' : ''
  })
  return out
}

// ───────────────────────── 수식 → 값 고정 (2026-10-09 YZY) ─────────────────────────
// 내려받는 청구서는 수식 없이 계산 결과값만 담는다. 서식·병합은 그대로, 셀 안의 수식(<f>)만 값으로 바꾼다.
// 지원: + - * / ^ % ( ), SUM·MIN·MAX·ABS·ROUND·ROUNDUP·ROUNDDOWN, 같은 시트·다른 시트(LCL!B1) 참조·범위.
// 못 푸는 수식은 수식 그대로 두고 console.error (조용히 틀린 값을 쓰지 않는다)

function colNum(s) { let n = 0; for (const ch of s) n = n * 26 + ch.charCodeAt(0) - 64; return n }
function colName(n) { let s = ''; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26) } return s }

// 시트 XML → { 셀주소: { f, v } } (v: 숫자는 Number, 글자는 String, 없으면 undefined)
function readCells(xml, sst) {
  const cells = {}
  ;(xml.match(/<c\b[^>]*\/>|<c\b[^>]*>[\s\S]*?<\/c>/g) || []).forEach(c => {
    const ref = (c.match(/^<c\b[^>]*\br="([A-Z]+\d+)"/) || [])[1]
    if (!ref) return
    const t = (c.match(/^<c\b[^>]*\bt="([^"]+)"/) || [])[1]
    const fm = c.match(/<f\b([^>]*?)(?:\/>|>([\s\S]*?)<\/f>)/)
    const vm = c.match(/<v>([^<]*)<\/v>/)
    let v
    if (t === 's' || t === 'inlineStr' || t === 'str') v = (t === 'str' && !vm) ? undefined : cellText(c, sst)
    else if (t === 'b') v = vm ? vm[1] === '1' : undefined
    else if (t === 'e') v = vm ? { err: vm[1] } : undefined
    else v = vm && vm[1] !== '' ? +vm[1] : undefined
    cells[ref] = { f: fm ? { attrs: fm[1], body: fm[2] === undefined ? '' : xmlUnesc(fm[2]) } : null, v }
  })
  return cells
}

// 엑셀 ROUND 계열 — 15자리로 먼저 맞춰 부동소수 찌꺼기(1.005*100 등)를 엑셀처럼 처리
function roundDec(x, d, mode) {
  const k = Math.pow(10, d)
  const y = Number((Math.abs(x) * k).toPrecision(15))
  const r = mode === 'up' ? Math.ceil(y) : mode === 'down' ? Math.floor(y) : Math.floor(y + 0.5)
  return Math.sign(x) * r / k
}

// sheets: { 시트이름: { 셀주소: {f,v} } }, cur: 현재 시트 이름
function evalFormula(body, cur, sheets, memo) {
  const src = body.replace(/^=/, '')
  const toks = []
  const re = /\s*(?:(\d+\.?\d*(?:[eE][+-]?\d+)?|\.\d+)|"([^"]*)"|((?:'[^']+'|(?:\[\d+\])?[^\s!+\-*/^%(),:'"]+)!)?(\$?[A-Z]{1,3}\$?\d+)(?::(\$?[A-Z]{1,3}\$?\d+))?(?![\w(])|([A-Z][A-Z0-9.]*)\(|([-+*/^%(),]))/y
  let pos = 0
  while (pos < src.length && src.slice(pos).trim()) {
    re.lastIndex = pos
    const m = re.exec(src)
    if (!m) throw new Error('unsupported: ' + src)
    pos = re.lastIndex
    if (m[1] !== undefined) toks.push({ k: 'n', v: +m[1] })
    else if (m[2] !== undefined) toks.push({ k: 's', v: m[2] })
    else if (m[4]) toks.push({ k: 'r', sheet: m[3], a: m[4], b: m[5] })
    else if (m[6]) toks.push({ k: 'f', v: m[6] })
    else toks.push({ k: 'o', v: m[7] })
  }
  let i = 0
  const sheetOf = t => {
    if (!t.sheet) return cur
    const nm = t.sheet.slice(0, -1).replace(/^\[\d+\]/, '').replace(/^'|'$/g, '').replace(/^\[\d+\]/, '')
    if (!sheets[nm]) throw new Error('no sheet ' + nm)
    return nm
  }
  const cellVal = (sh, ref) => {
    ref = ref.replace(/\$/g, '')
    const key = sh + '!' + ref
    if (memo.has(key)) { const r = memo.get(key); if (r === undefined) throw new Error('cycle ' + key); return r }
    const cell = (sheets[sh] || {})[ref]
    if (!cell) return ''
    if (cell.f && cell.v === undefined) {
      memo.set(key, undefined)
      const r = evalFormula(cell.f.body, sh, sheets, memo)
      memo.set(key, r)
      return r
    }
    return cell.v === undefined ? '' : cell.v
  }
  const num = v => {
    if (v === undefined || v === '') return 0
    if (typeof v === 'number') return v
    if (typeof v === 'boolean') return v ? 1 : 0
    if (typeof v === 'string' && !isNaN(+v)) return +v
    throw new Error('non-number operand')
  }
  const list = t => { // 범위·단일 참조 → 값 배열
    const sh = sheetOf(t)
    if (!t.b) return [cellVal(sh, t.a)]
    const [, c1, r1] = t.a.replace(/\$/g, '').match(/^([A-Z]+)(\d+)$/)
    const [, c2, r2] = t.b.replace(/\$/g, '').match(/^([A-Z]+)(\d+)$/)
    const out = []
    for (let r = +r1; r <= +r2; r++) for (let c = colNum(c1); c <= colNum(c2); c++) out.push(cellVal(sh, colName(c) + r))
    return out
  }
  const isOp = v => toks[i] && toks[i].k === 'o' && toks[i].v === v
  function expr() {
    let a = term()
    while (isOp('+') || isOp('-')) { const op = toks[i++].v; const b = term(); a = op === '+' ? num(a) + num(b) : num(a) - num(b) }
    return a
  }
  function term() {
    let a = power()
    while (isOp('*') || isOp('/')) {
      const op = toks[i++].v; const b = power()
      if (op === '/') { if (num(b) === 0) throw new Error('div0'); a = num(a) / num(b) } else a = num(a) * num(b)
    }
    return a
  }
  function power() {
    let a = unary()
    while (isOp('^')) { i++; a = Math.pow(num(a), num(unary())) }
    return a
  }
  function unary() {
    if (isOp('-')) { i++; return -num(unary()) }
    if (isOp('+')) { i++; return unary() }
    let a = atom()
    while (isOp('%')) { i++; a = num(a) / 100 }
    return a
  }
  function atom() {
    const t = toks[i++]
    if (!t) throw new Error('unexpected end')
    if (t.k === 'n' || t.k === 's') return t.v
    if (t.k === 'r') {
      if (t.b) throw new Error('range outside function')
      return list(t)[0]
    }
    if (t.k === 'o' && t.v === '(') { const v = expr(); if (!isOp(')')) throw new Error('missing )'); i++; return v }
    if (t.k === 'f') {
      const args = []
      if (isOp(')')) i++
      else {
        for (;;) {
          const nt = toks[i]
          if (nt && nt.k === 'r' && nt.b) { i++; args.push({ arr: list(nt) }) } else args.push(expr())
          if (isOp(',')) { i++; continue }
          if (isOp(')')) { i++; break }
          throw new Error('bad args')
        }
      }
      // 범위 안 글자·빈칸은 합계에서 빠지고, 직접 넣은 값은 숫자로 본다(엑셀 SUM 과 같다)
      const nums = () => {
        const o = []
        args.forEach(x => (x && x.arr ? x.arr.forEach(v => { if (typeof v === 'number') o.push(v) }) : o.push(num(x))))
        return o
      }
      const fn = t.v
      if (fn === 'SUM') return nums().reduce((p, c) => p + c, 0)
      if (fn === 'MIN') { const n = nums(); return n.length ? Math.min(...n) : 0 }
      if (fn === 'MAX') { const n = nums(); return n.length ? Math.max(...n) : 0 }
      if (fn === 'ABS') return Math.abs(num(args[0]))
      if (fn === 'ROUND' || fn === 'ROUNDUP' || fn === 'ROUNDDOWN') {
        return roundDec(num(args[0]), args.length > 1 ? num(args[1]) : 0, fn === 'ROUNDUP' ? 'up' : fn === 'ROUNDDOWN' ? 'down' : 'half')
      }
      throw new Error('unsupported function ' + fn)
    }
    throw new Error('unexpected token')
  }
  const out = expr()
  if (i < toks.length) throw new Error('trailing tokens')
  return out
}

// 시트 XML 의 수식 셀을 값 셀로 바꾼다. 스타일(s)은 그대로. 못 푸는 수식은 그대로 두고 console.error
function freezeFormulas(xml, name, sheets, memo) {
  return xml.replace(/<c\b[^>]*\/>|<c\b[^>]*>[\s\S]*?<\/c>/g, c => {
    if (!/<f[\s>/]/.test(c)) return c
    const open = c.match(/^<c\b[^>]*?>/)[0]
    const ref = (open.match(/\br="([^"]+)"/) || [])[1]
    const st = (open.match(/\bs="([^"]+)"/) || [])[1]
    const attrs = 'r="' + ref + '"' + (st !== undefined ? ' s="' + st + '"' : '')
    const cell = (sheets[name] || {})[ref]
    try {
      if (!cell || !cell.f) throw new Error('no cell')
      // 캐시값이 있으면(LCL 시트) 엑셀이 계산해 둔 값이라 그걸 쓴다. 없으면(서식 시트) 여기서 계산
      const v = cell.v !== undefined ? cell.v : evalFormula(cell.f.body, name, sheets, memo)
      if (v && typeof v === 'object' && v.err) return '<c ' + attrs + ' t="e"><v>' + v.err + '</v></c>'
      if (typeof v === 'number') {
        if (!isFinite(v)) throw new Error('not finite')
        return '<c ' + attrs + '><v>' + Number(v.toPrecision(15)) + '</v></c>'
      }
      if (typeof v === 'boolean') return '<c ' + attrs + ' t="b"><v>' + (v ? 1 : 0) + '</v></c>'
      return '<c ' + attrs + ' t="inlineStr"><is><t xml:space="preserve">' + xmlEsc(v) + '</t></is></c>'
    } catch (e) {
      console.error('[lclBillXlsx] 수식을 값으로 못 바꿔 수식 그대로 둡니다 ' + name + '!' + ref + ' : ' + (e && e.message))
      return c
    }
  })
}

// 고객 한 명의 xlsx(Uint8Array). 원본 zip 을 매번 다시 읽어 고친다(공유 객체를 건드리지 않게)
export function buildCustomerBill(u8, parsed, row, keepFormulas = false) {
  const { cfb, get } = readZip(u8)
  const sst = sharedStrings(get)
  const put = (p, s) => {
    const e = XLSX.CFB.find(cfb, '/' + p)
    if (e) e.content = enc.encode(s)
    else XLSX.CFB.utils.cfb_add(cfb, '/' + p, enc.encode(s))
  }
  const tplPath = parsed.paths[parsed.template]
  const dataPath = parsed.paths[parsed.data]
  const tplXml = fillTemplateXml(get(tplPath), row, parsed.mapping, sst)
  const dataXml = keepOneRowXml(get(dataPath), parsed.headerRow, row.sheetRow)
  // 수식은 계산값으로 고정(YZY 10-09) — 서식 시트 수식이 LCL 시트 칸(환율 등)을 보므로 두 시트를 함께 푼다
  const sheets = { [parsed.template]: readCells(tplXml, sst), [parsed.data]: readCells(dataXml, sst) }
  const memo = new Map()
  // keepFormulas=true(「保留公式」 체크)면 수식을 그대로 둔다 — 열 때 다시 계산(아래 fullCalcOnLoad)
  put(tplPath, keepFormulas ? tplXml : freezeFormulas(tplXml, parsed.template, sheets, memo))
  put(dataPath, keepFormulas ? dataXml : freezeFormulas(dataXml, parsed.data, sheets, memo))

  // 수식 값 캐시를 지웠으니 열 때 다시 계산. calcChain 은 지운 행을 가리킬 수 있어 뺀다(엑셀이 다시 만든다)
  let wbXml = get('xl/workbook.xml')
  if (/<calcPr\b/.test(wbXml)) {
    wbXml = wbXml.replace(/<calcPr\b([^>]*?)\/?>/, (m, a) => '<calcPr' + a.replace(/\s*fullCalcOnLoad="[^"]*"/, '') + ' fullCalcOnLoad="1"/>')
  } else {
    wbXml = wbXml.replace(/(<\/sheets>(?:[\s\S]*?<\/definedNames>)?)/, '$1<calcPr fullCalcOnLoad="1"/>')
  }
  // 열면 첫 번째 시트가 보이게(YZY 10-09): activeTab=0, tabSelected 는 첫 시트에만(둘 이상이면 엑셀이 시트 그룹으로 연다)
  wbXml = wbXml.replace(/<workbookView\b([^>]*?)(\/?)>/, (m, a, sl) => '<workbookView' + a.replace(/\s*activeTab="[^"]*"/, '').replace(/\s*firstSheet="[^"]*"/, '') + ' activeTab="0"' + sl + '>')
  put('xl/workbook.xml', wbXml)
  const sheetFiles = Object.values(parsed.paths) // 통합문서 시트 순서대로 — 첫 번째가 맨 앞 탭
  sheetFiles.forEach(p => {
    const e = XLSX.CFB.find(cfb, '/' + p)
    if (!e || !e.content) return
    let x = dec.decode(e.content instanceof Uint8Array ? e.content : new Uint8Array(e.content))
    x = x.replace(/<sheetView\b([^>]*?)(\/?)>/, (m, a, sl) => '<sheetView' + a.replace(/\s*tabSelected="[^"]*"/, '') + (p === sheetFiles[0] ? ' tabSelected="1"' : '') + sl + '>')
    put(p, x)
  })
  if (XLSX.CFB.find(cfb, '/xl/calcChain.xml')) {
    XLSX.CFB.utils.cfb_del(cfb, '/xl/calcChain.xml')
    put('xl/_rels/workbook.xml.rels', get('xl/_rels/workbook.xml.rels').replace(/<Relationship\b[^>]*calcChain[^>]*\/>/g, ''))
    put('[Content_Types].xml', get('[Content_Types].xml').replace(/<Override\b[^>]*calcChain[^>]*\/>/g, ''))
  }
  // 공유문자열에 다른 고객 이름이 남는다(LCL 시트 A열) — 남은 셀이 안 쓰는 항목은 비운다. 번호는 그대로 둔다
  const used = new Set()
  ;[tplPath, dataPath].forEach(p => {
    get(p).replace(/<c\b[^>]*\bt="s"[^>]*>\s*<v>(\d+)<\/v>/g, (m, i) => { used.add(+i); return m })
  })
  const sstXml = get('xl/sharedStrings.xml')
  if (sstXml) {
    let i = -1
    put('xl/sharedStrings.xml', sstXml.replace(/<si>[\s\S]*?<\/si>|<si\/>/g, m => { i += 1; return used.has(i) ? m : '<si><t></t></si>' }))
  }

  const out = XLSX.CFB.write(cfb, { fileType: 'zip', type: 'array', compression: true })
  return out instanceof Uint8Array ? out : new Uint8Array(out)
}

export default { parseBillWorkbook, buildMapping, valueFor, rowDate, billFileName, zipFileName, safeFileName, buildCustomerBill, phKey }
